import { DefaultWorklistASAMs } from "enums/Methods";
import {
  getColIndexFromSelectedWell,
  getPlateWellIdFromRowAndColIndex,
  getRowAndColCountByLabwareType,
  getRowIndexFromSelectedWell,
  getSourcePlateInfo,
  getValueInDetailsString,
} from "features/WorklistTools/shared/WorklistHelpers";
import { Container } from "graphql/generated/schema-types";
import NucleofectionWorklistService, {
  GetASAMAndPlateInfoData,
} from "services/NucleofectionWorklistToolService";
import { DestTransferStrategy } from "../../NucleofectionInterfaces";
import {
  CompoundPlateInfoState,
  DestPlateInfoState,
  IntermediatePlateInfoState,
  NucleofectionPlateInfoState,
  SourcePlateInfoState,
  WorklistWellMappingState,
} from "../../state";
import {
  getCompoundPlateInfoState,
  getIntermediatePlateInfoState,
  getNucleofectionPlateInfoState,
  getSourcePlateInfoState,
} from "../../state-helpers";
import { INucleofectionMethodSettings } from "../../state/initial-state";

export interface UploadedWorklist {
  task: string;
  details: string;
  aspiratevol: string;
  dispensevol: string;
  transfervol: string;
  sourceplatetype: string;
  sourceplatebarcode: string;
  sourcewellid: string;
  destinationplatetype: string;
  destinationplatebarcode: string;
  destinationwellid: string;
  rownumber: string;
}

export const parsePlateInfo = (
  uploadedWorklist: UploadedWorklist[],
  key: keyof UploadedWorklist
) => {
  return [
    ...new Map(uploadedWorklist.map((rows) => [rows[key], rows])).values(),
  ];
};

const mapWorklistRows = (
  row: UploadedWorklist,
  sourcePlateIndex: number,
  destPlateIndex: number
) => {
  return {
    sourcePlateIndex: sourcePlateIndex,
    sourceWellId: row.sourcewellid,
    destPlateIndex: destPlateIndex,
    destWellId: row.destinationwellid,
    transferVol: String(row.transfervol) ?? "",
  };
};

export const parseUploadedWorklist = async (
  worklistRows: UploadedWorklist[],
  fetchNucPlateInfo: (plateBarcode: string) => Promise<any>
) => {
  const methodDataString =
    worklistRows.find((e) => e.task === "MethodData")?.details ?? "";

  const methodReservationId = getValueInDetailsString(
    methodDataString,
    "MethodReservationId"
  );

  const { data: asamAndPlateData } =
    await NucleofectionWorklistService.getASAMAndPlateInfo(methodReservationId);

  const requiredTasks = ["SrcToNuc", "NucToInt", "IntToDest"];
  const missingRequiredTasks = requiredTasks.filter(
    (task) => !worklistRows.some((row) => row.task === task)
  );

  if (missingRequiredTasks.length > 0) {
    throw new Error(
      `Missing required tasks in worklist: ${missingRequiredTasks.join(", ")}`
    );
  }

  const compoundPlateRows = worklistRows.filter(
    (e) => e.task === "CompoundPlate"
  );
  const sourceToNucRows = worklistRows.filter((e) => e.task === "SrcToNuc");
  const nucToIntRows = worklistRows.filter((row) => row.task === "NucToInt");
  const intToDestRows = worklistRows.filter((e) => e.task === "IntToDest");
  const nucWashRow = worklistRows.find((e) => e.task === "NucWash");

  const washDetails = nucWashRow?.details || "";
  const washVol = Number(getValueInDetailsString(washDetails, "WashVol")) || 0;
  const numberOfWashes =
    Number(getValueInDetailsString(washDetails, "WashCycles")) || 0;

  const sourcePlateInfo: SourcePlateInfoState[] = [getSourcePlateInfoState()];
  const compoundPlateInfo: CompoundPlateInfoState[] = [
    getCompoundPlateInfoState(),
  ];
  const nucPlateInfo: NucleofectionPlateInfoState[] = [
    getNucleofectionPlateInfoState(),
  ];
  const intPlateInfo: IntermediatePlateInfoState[] = [
    getIntermediatePlateInfoState(),
  ];

  const uniqueDestinationPlates = parsePlateInfo(
    intToDestRows,
    "destinationplatebarcode"
  );

  const destPlateInfo: DestPlateInfoState[] =
    uniqueDestinationPlates.length > 0
      ? uniqueDestinationPlates.map((_, index) => ({
          index: index,
          plateBarcode: "",
          labwareTypeCode: "",
          operatingVol: 0,
          minOperatingVol: 0,
          preprocess: true,
          topup: true,
          rows: [],
          cols: [],
        }))
      : [
          {
            index: 0,
            plateBarcode: "",
            labwareTypeCode: "",
            operatingVol: 0,
            minOperatingVol: 0,
            preprocess: true,
            topup: true,
            rows: [],
            cols: [],
          },
        ];

  if (sourceToNucRows.length > 0) {
    const sourceRow = sourceToNucRows[0];
    const sourcePlateWells = await getSourcePlateInfo(
      0,
      sourceRow.sourceplatebarcode,
      false
    );

    const wellInfo = sourcePlateWells.map((srcWell) => {
      return {
        barcode: srcWell.barcode,
        rowPos: srcWell.rowPos,
        colPos: srcWell.colPos,
        sampleId: srcWell.sampleBarcode,
        color: srcWell.color,
      };
    });

    const rowColCount = getRowAndColCountByLabwareType(
      sourceRow.sourceplatetype
    );
    const sourcePlateData = asamAndPlateData.acceptablePlateTypes.find(
      (plate) =>
        plate.labwareType.arrayMethodPlateRoleId === 1 &&
        plate.labwareType.labwareTypeCode === sourceRow.sourceplatetype
    );

    sourcePlateInfo[0] = {
      plateBarcode: sourceRow.sourceplatebarcode,
      labwareTypeCode: sourceRow.sourceplatetype,
      operatingVol:
        sourcePlateData?.labwareType?.defaultLabwareVolume ??
        rowColCount.operatingVol,
      rows: rowColCount.rows,
      cols: rowColCount.cols,
      wellInfo: wellInfo,
    };
  }

  if (compoundPlateRows.length > 0) {
    const compoundRow = compoundPlateRows[0];
    const rowColCount = getRowAndColCountByLabwareType(
      compoundRow.sourceplatetype
    );
    const compoundPlateData = asamAndPlateData.acceptablePlateTypes.find(
      (plate) =>
        plate.labwareType.arrayMethodPlateRoleId === 5 &&
        plate.labwareType.labwareTypeCode === compoundRow.sourceplatetype
    );

    compoundPlateInfo[0] = {
      index: 0,
      plateBarcode: compoundRow.sourceplatebarcode,
      labwareTypeCode: compoundRow.sourceplatetype,
      operatingVol:
        compoundPlateData?.labwareType?.maxLabwareVolume ??
        rowColCount.operatingVol,
      rows: rowColCount.rows,
      cols: rowColCount.cols,
      wellInfo: sourcePlateInfo[0]?.wellInfo || [],
      transferVol: compoundRow.transfervol.toString() || "",
    };
  }

  if (sourceToNucRows.length > 0) {
    const nucRow = sourceToNucRows[0];
    const rowColCount = getRowAndColCountByLabwareType(
      nucRow.destinationplatetype
    );
    const nucPlateData = asamAndPlateData.acceptablePlateTypes.find(
      (plate) =>
        plate.labwareType.arrayMethodPlateRoleId === 3 &&
        plate.labwareType.labwareTypeCode === nucRow.destinationplatetype
    );

    let nucTopUpVol = 0;
    const topUpVolRow = nucToIntRows.find((row) =>
      row.details?.includes("TopUpVol")
    );
    if (topUpVolRow?.details) {
      nucTopUpVol =
        parseInt(getValueInDetailsString(topUpVolRow.details, "TopUpVol")) || 0;
    }

    nucPlateInfo[0] = {
      index: 0,
      plateBarcode: nucRow.destinationplatebarcode,
      labwareTypeCode: nucRow.destinationplatetype,
      operatingVol:
        nucPlateData?.labwareType?.maxLabwareVolume ?? rowColCount.operatingVol,
      minOperatingVol: nucPlateData?.labwareType?.minLabwareVolume ?? 25,
      rows: rowColCount.rows,
      cols: rowColCount.cols,
      numberOfWashes: numberOfWashes,
      topUpVol: nucTopUpVol,
      wellInfo: [],
      washVol: washVol,
    };
  }

  if (nucToIntRows.length > 0) {
    const intRow = nucToIntRows[0];
    const rowColCount = getRowAndColCountByLabwareType(
      intRow.destinationplatetype
    );
    const intPlateData = asamAndPlateData.acceptablePlateTypes.find(
      (plate) =>
        plate.labwareType.arrayMethodPlateRoleId === 7 &&
        plate.labwareType.labwareTypeCode === intRow.destinationplatetype
    );

    intPlateInfo[0] = {
      plateBarcode: intRow.destinationplatebarcode,
      labwareTypeCode: intRow.destinationplatetype,
      operatingVol: intPlateData?.labwareType?.defaultLabwareVolume ?? 0,
      rows: rowColCount.rows,
      cols: rowColCount.cols,
    };
  }

  for (const [index, uniqueDestPlate] of uniqueDestinationPlates.entries()) {
    const rowColCount = getRowAndColCountByLabwareType(
      uniqueDestPlate.destinationplatetype
    );
    const uniqueDestPlateData = asamAndPlateData.acceptablePlateTypes.find(
      (plate) =>
        plate.labwareType.arrayMethodPlateRoleId === 2 &&
        plate.labwareType.labwareTypeCode ===
          uniqueDestPlate.destinationplatetype
    );

    const destRow = intToDestRows.find(
      (row) =>
        row.destinationplatebarcode === uniqueDestPlate.destinationplatebarcode
    );

    let preprocess = true;
    let topup = true;

    if (destRow?.details) {
      preprocess = !!parseInt(
        getValueInDetailsString(destRow.details, "Preprocess")
      );
      topup = !!parseInt(getValueInDetailsString(destRow.details, "Topup"));
    }

    destPlateInfo[index] = {
      index: index,
      plateBarcode: uniqueDestPlate.destinationplatebarcode,
      labwareTypeCode: uniqueDestPlate.destinationplatetype,
      operatingVol: uniqueDestPlateData?.labwareType?.defaultLabwareVolume ?? 0,
      minOperatingVol: uniqueDestPlateData?.labwareType?.minLabwareVolume ?? 0,
      rows: rowColCount.rows,
      cols: rowColCount.cols,
      preprocess: preprocess,
      topup: topup,
    };
  }

  const sourceToNuc = sourceToNucRows.map((row) => mapWorklistRows(row, 0, 0));

  // Add well info to nuc plate
  if (sourcePlateInfo[0].wellInfo.length > 0 && sourceToNucRows.length > 0) {
    const nucBarcode = sourceToNucRows[0].destinationplatebarcode;
    const nucPlateResponse = await fetchNucPlateInfo(nucBarcode);
    const nucContainers = nucPlateResponse?.platesInfo?.containers ?? [];

    type nucWellInfo = {
      barcode: string | null;
      rowPos: number;
      colPos: number;
      sampleId?: number | string;
      sampleBarcode?: string;
      color?: string;
    };

    const usedNucWells: nucWellInfo[] = nucContainers.map(
      (container: Container) => {
        return {
          barcode: container.barcode,
          rowPos: container.rowPos,
          colPos: container.colPos,
          sampleId: container.sample?.sampleId,
          color: "#454545",
        };
      }
    );

    const worklistNucWells = sourceToNuc
      .map((mapping) => {
        const sourceWell = sourcePlateInfo[0].wellInfo.find(
          (well) =>
            getPlateWellIdFromRowAndColIndex(
              well.rowPos - 1,
              well.colPos - 1
            ) === mapping.sourceWellId
        );
        if (sourceWell) {
          const nucRow = getRowIndexFromSelectedWell(mapping.destWellId) + 1;
          const nucCol = getColIndexFromSelectedWell(mapping.destWellId) + 1;
          return {
            ...sourceWell,
            rowPos: nucRow,
            colPos: nucCol,
          };
        }
        return null;
      })
      .filter(Boolean) as nucWellInfo[];

    const occupied = new Set(
      usedNucWells.map(({ rowPos, colPos }) => `${rowPos}:${colPos}`)
    );
    const overlaps = worklistNucWells.filter(({ rowPos, colPos }) =>
      occupied.has(`${rowPos}:${colPos}`)
    );

    if (overlaps.length > 0) {
      throw new Error(
        `Invalid worklist: ${overlaps.length} Overlap between existing and new Nuc well(s) in plate ${nucBarcode}.`
      );
    }

    const fullNucPlate = [...usedNucWells, ...worklistNucWells];

    nucPlateInfo[0].wellInfo = fullNucPlate;
  }

  const nucToInt = nucToIntRows.map((row) => mapWorklistRows(row, 0, 0));

  // If no explicit nucToInt mappings, create them based on sourceToNuc (normal flow logic)
  if (nucToInt.length === 0 && sourceToNuc.length > 0) {
    let row = 1;
    let col = 1;
    for (const well of sourceToNuc) {
      if (row === 7) {
        row = 1;
        col++;
      }
      if (col === 11) break;
      const wellMapping = {
        sourcePlateIndex: well.destPlateIndex,
        sourceWellId: well.destWellId,
        destPlateIndex: 0,
        destWellId: getPlateWellIdFromRowAndColIndex(row, col),
        transferVol: "" as string,
      };
      nucToInt.push(wellMapping);
      row++;
    }
  }

  const intToDest: WorklistWellMappingState[] = [];

  //Single plate compilers
  let currentPlateBarcode: string | null = null;
  let currentPlateRows: UploadedWorklist[] = [];

  const flushPreviousPlate = () => {
    if (!currentPlateRows.length) return;
    const prevBarcode = currentPlateRows[0].destinationplatebarcode;
    const prevDestIndex = uniqueDestinationPlates.findIndex(
      (p) => p.destinationplatebarcode === prevBarcode
    );

    for (const row of currentPlateRows) {
      intToDest.push(mapWorklistRows(row, 0, prevDestIndex));
    }

    currentPlateBarcode = null;
    currentPlateRows = [];
  };

  for (const intToDestRow of intToDestRows) {
    const destIndex = uniqueDestinationPlates.findIndex(
      (plate) =>
        plate.destinationplatebarcode === intToDestRow.destinationplatebarcode
    );

    //96w plate
    if (getValueInDetailsString(intToDestRow.details, "Stamp") === "1") {
      const currentPlateMapping = nucToInt.map((nucToIntMapping) => ({
        sourcePlateIndex: 0, //Only one nuc plate
        sourceWellId: nucToIntMapping.destWellId, // matches position in intPlate
        destPlateIndex: destIndex,
        destWellId: nucToIntMapping.destWellId, // matches position in intPlate
        transferVol: String(intToDestRow.transfervol),
      }));
      intToDest.push(...currentPlateMapping);
      continue;
    }

    //12w or 24w plate
    if (currentPlateBarcode == null) {
      //First plate
      currentPlateBarcode = intToDestRow.destinationplatebarcode;
      currentPlateRows.push(intToDestRow);
    } else if (intToDestRow.destinationplatebarcode === currentPlateBarcode) {
      //Continue same plate
      currentPlateRows.push(intToDestRow);
    } else {
      //Start new plate
      flushPreviousPlate();
      currentPlateBarcode = intToDestRow.destinationplatebarcode;
      currentPlateRows.push(intToDestRow);
    }
  }

  flushPreviousPlate();

  const methodSettings = parseMethodSettingsFromWorklist(
    worklistRows,
    asamAndPlateData
  );

  const parsedWorklistInfo = {
    sourcePlateInfo,
    compoundPlateInfo,
    nucPlateInfo,
    intPlateInfo,
    destPlateInfo,
    worklistValues: {
      sourceToNuc: sourceToNuc,
      nucToInt: nucToInt,
      intToDest: intToDest,
    },
    methodSettings,
  };
  return parsedWorklistInfo;
};
//method setting from worklist - start hereeeee
const parseMethodSettingsFromWorklist = (
  uploadedWorklist: UploadedWorklist[],
  asamAndPlateData: GetASAMAndPlateInfoData
) => {
  const getWorklistRowByTask = (task: string) =>
    uploadedWorklist.find((e) => e.task === task);

  let destSampleType = "";
  const destMappingRow = getWorklistRowByTask("DestMappingFileAppendation");
  if (destMappingRow?.details) {
    const sampleTypeMatch = destMappingRow.details.match(
      /_FORCE_SAMPLETYPE_([A-Z]+)/
    );
    if (sampleTypeMatch && sampleTypeMatch[1]) {
      destSampleType = sampleTypeMatch[1];
    }
  } else if (!destMappingRow) {
    destSampleType = "None";
  }

  const arraySystemAutomationMethodId = DefaultWorklistASAMs.Nucleofection;

  const methodSettings: INucleofectionMethodSettings = {
    selectedSystem: asamAndPlateData.system ?? 3,
    arraySystemAutomationMethodId: arraySystemAutomationMethodId,
    destTransferStrategy: DestTransferStrategy.Custom,
    destSampleType: destSampleType,
  };

  return methodSettings;
};
